import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Radar,
  FlaskConical,
  ShieldCheck,
  ArrowRight,
  FileSearch,
  GitCompareArrows,
  ScrollText,
  Check,
  Lock,
  Eye,
  Sparkles,
  CalendarClock,
  FileText,
  BadgeCheck,
  Landmark,
  ListChecks,
} from "lucide-react";
import { Button, BrandLockup, HenosisWordmark } from "@/components/ui/primitives";

/* --------------------------------------------------------------- helpers */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-brand-400 mb-3 text-[11px] font-semibold uppercase tracking-[0.18em]">
      {children}
    </div>
  );
}

function Grain() {
  return <div aria-hidden className="argus-grain pointer-events-none absolute inset-0" />;
}

/** Count-up stat that animates when scrolled into view. */
function useCountUp(target: number, durationMs = 1400) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const t0 = performance.now();
            const tick = (now: number) => {
              const p = Math.min(1, (now - t0) / durationMs);
              setValue(target * (1 - Math.pow(1 - p, 3)));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, durationMs]);
  return { ref, value };
}

function StatCounter({ to, suffix = "", label }: { to: number; suffix?: string; label: string }) {
  const { ref, value } = useCountUp(to);
  return (
    <div ref={ref} className="space-y-1.5">
      <div className="text-4xl font-semibold tabular-nums tracking-tight text-neutral-50">
        {Math.round(value)}
        {suffix}
      </div>
      <div className="text-sm text-neutral-400">{label}</div>
    </div>
  );
}

function StaticStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="space-y-1.5">
      <div className="text-4xl font-semibold tracking-tight text-neutral-50">{value}</div>
      <div className="text-sm text-neutral-400">{label}</div>
    </div>
  );
}

/** Framed product screenshot with browser-style chrome. */
function ProductFrame({
  src,
  alt,
  className = "",
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-neutral-800/80 bg-neutral-900/40 shadow-2xl shadow-black/40 ${className}`}
    >
      <div className="flex items-center gap-1.5 border-b border-neutral-800/70 bg-neutral-950/60 px-3 py-2">
        <span className="size-2 rounded-full bg-neutral-700" />
        <span className="size-2 rounded-full bg-neutral-700" />
        <span className="size-2 rounded-full bg-neutral-700" />
      </div>
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
    </div>
  );
}

const DEMO_TO = "/sign-in";
const CONTACT = "mailto:hello@henosis.solutions?subject=Henosis%20Argus%20—%20demo%20request";

/* ------------------------------------------------------------------ page */

export function LandingPage() {
  return (
    <div className="bg-neutral-975 min-h-dvh text-neutral-100">
      {/* Nav */}
      <nav className="bg-neutral-975/80 sticky top-0 z-30 border-b border-neutral-900/60 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3.5">
          <BrandLockup size="sm" />
          <div className="hidden items-center gap-7 text-sm text-neutral-400 md:flex">
            <a href="#platform" className="transition hover:text-neutral-100">
              Platform
            </a>
            <a href="#why" className="transition hover:text-neutral-100">
              Why Argus
            </a>
            <a href="#how" className="transition hover:text-neutral-100">
              How it works
            </a>
            <a href="#security" className="transition hover:text-neutral-100">
              Security
            </a>
            <a href="#pricing" className="transition hover:text-neutral-100">
              Pricing
            </a>
            <a href="#faq" className="transition hover:text-neutral-100">
              FAQ
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/sign-in">
              <Button variant="ghost" size="sm">
                Sign in
              </Button>
            </Link>
            <Link to="/sign-in">
              <Button variant="primary" size="sm">
                Live demo <ArrowRight className="size-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="argus-mesh relative overflow-hidden border-b border-neutral-900">
        <Grain />
        <div className="relative mx-auto max-w-6xl px-6 pb-4 pt-20 sm:pt-28">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-neutral-800 bg-neutral-900/60 px-3 py-1 text-xs text-neutral-300">
              <span className="bg-brand-400 flex size-1.5 rounded-full" />
              The AI compliance platform for private fund GPs
            </div>
            <h1 className="text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-neutral-50 sm:text-6xl">
              Obligations multiply.
              <br className="hidden sm:block" /> Surprises shouldn&apos;t.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-balance text-lg leading-relaxed text-neutral-300">
              Argus reads your side letters, tracks every filing and deadline, drafts your Form PF,
              and screens conduct — each with a human in the loop and an audit trail built for the
              SEC exam. The whole compliance program, on one platform.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to={DEMO_TO}>
                <Button variant="primary" size="md">
                  Explore the live demo <ArrowRight className="size-4" />
                </Button>
              </Link>
              <a href={CONTACT}>
                <Button variant="secondary" size="md">
                  Book a walkthrough
                </Button>
              </a>
            </div>
            <p className="mt-4 text-xs text-neutral-500">
              Human-in-the-loop · clause-level provenance · append-only audit trail
            </p>
          </div>

          {/* Hero product card — tilted, glass-framed */}
          <div className="relative mx-auto mt-14 max-w-5xl [perspective:2000px]">
            <div className="shadow-brand-950/30 origin-bottom overflow-hidden rounded-xl border border-neutral-800/80 shadow-2xl [transform:rotateX(6deg)]">
              <img
                src="/marketing/dashboard.png"
                alt="Argus compliance command center — conflict radar, posture score, and the deadline calendar"
                className="block w-full"
              />
            </div>
            <div className="from-neutral-975 pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t to-transparent" />
          </div>
        </div>
      </header>

      {/* Trust bar */}
      <section className="border-b border-neutral-900 bg-neutral-950/30">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 py-6 text-xs text-neutral-500">
          <span className="flex items-center gap-2">
            <Sparkles className="size-3.5 text-neutral-600" /> Powered by Claude
          </span>
          <span className="hidden h-3 w-px bg-neutral-800 sm:block" />
          <span className="flex items-center gap-2">
            <Eye className="size-3.5 text-neutral-600" /> Human-in-the-loop by design
          </span>
          <span className="hidden h-3 w-px bg-neutral-800 sm:block" />
          <span className="flex items-center gap-2">
            <ShieldCheck className="size-3.5 text-neutral-600" /> Your data is never trained on
          </span>
          <span className="hidden h-3 w-px bg-neutral-800 sm:block" />
          <span className="flex items-center gap-2">
            <Lock className="size-3.5 text-neutral-600" /> Multi-tenant RLS isolation
          </span>
        </div>
      </section>

      {/* Problem */}
      <section className="mx-auto max-w-5xl px-6 py-20 text-center">
        <SectionLabel>The problem</SectionLabel>
        <h2 className="mx-auto max-w-3xl text-balance text-3xl font-semibold tracking-tight text-neutral-50">
          A fund&apos;s compliance program is dozens of recurring promises and deadlines — each one
          a fiduciary duty.
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-neutral-400">
          Side letters with MFN rights. Form PF and Form ADV. Code-of-ethics attestations.
          Pay-to-play limits. Annual reviews and reporting deadlines. Each carries an obligation, a
          date, and an audit expectation — tracked today across spreadsheets, inboxes, and outside
          counsel at $2,000+/hour. One missed clause or deadline is a fiduciary breach and an SEC
          deficiency.
        </p>
        <div className="mt-12 grid gap-4 text-left sm:grid-cols-2 lg:grid-cols-4">
          {[
            [
              "Never miss a deadline",
              "Every filing, attestation, and notice on one calendar with lead-time alerts.",
            ],
            ["Never miss an MFN trigger", "Know the moment a new term entitles an existing LP."],
            [
              "File in a fraction of the time",
              "Form PF auto-populated from your data; AI drafts the rest.",
            ],
            ["Prove every decision", "An append-only record built for the SEC exam."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-xl border border-neutral-800/80 bg-neutral-900/40 p-5">
              <Check className="mb-2.5 size-4 text-emerald-400" />
              <div className="text-sm font-medium text-neutral-100">{t}</div>
              <div className="mt-1 text-xs leading-relaxed text-neutral-500">{b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Platform — the breadth */}
      <section id="platform" className="border-t border-neutral-900 bg-neutral-950/30">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center">
            <SectionLabel>The platform</SectionLabel>
            <h2 className="mx-auto max-w-2xl text-balance text-3xl font-semibold tracking-tight text-neutral-50">
              One platform for the whole compliance program
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-neutral-400">
              Everything a fund CCO or GC has to track, draft, and prove — in one system of record,
              with AI doing the first pass and a person signing off.
            </p>
          </div>

          {/* Four flagship modules, each with a live screenshot */}
          <div className="mt-12 grid gap-4 lg:grid-cols-2">
            <ModuleCard
              icon={CalendarClock}
              title="Compliance calendar"
              body="Every regulatory and side-letter deadline in one place, bucketed by urgency, with email reminders so nothing slips."
              src="/marketing/calendar.png"
              alt="Compliance calendar with overdue, due-soon, and scheduled obligations"
            />
            <ModuleCard
              icon={FileText}
              title="Regulatory filings"
              body="Form PF auto-populated from your platform data, AI-drafted where judgment is needed, gap-flagged where it isn't, and filing-ready. Form ADV next."
              src="/marketing/filings.png"
              alt="Form PF filing workspace with auto, AI-drafted, and gap-flagged fields"
            />
            <ModuleCard
              icon={BadgeCheck}
              title="Code of ethics"
              body="Run acknowledgement campaigns for the Code of Ethics, holdings reports, and conduct policies — and chase the stragglers to sign-off."
              src="/marketing/ethics.png"
              alt="Code of ethics attestation campaign with sign-off progress"
            />
            <ModuleCard
              icon={Landmark}
              title="Political contributions"
              body="Log every contribution and screen it against the Rule 206(4)-5 pay-to-play de-minimis limits — over-limit entries flagged automatically."
              src="/marketing/contributions.png"
              alt="Political contribution pay-to-play screening log"
            />
          </div>

          {/* Supporting modules — no screenshot */}
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {[
              [
                Radar,
                "Side-letter & MFN intelligence",
                "Extraction, conflict radar, what-if simulator, and the LP comparison matrix.",
              ],
              [
                ListChecks,
                "Requirements & checklists",
                "Instantiate the SEC/IRS requirements that apply from a built-in catalog in one click.",
              ],
              [
                ScrollText,
                "Append-only audit trail",
                "Every action recorded at the database level; export a per-fund evidence pack in a click.",
              ],
            ].map(([Icon, t, b]) => {
              const I = Icon as React.ComponentType<{ className?: string }>;
              return (
                <div
                  key={t as string}
                  className="rounded-xl border border-neutral-800/80 bg-neutral-900/40 p-5"
                >
                  <I className="text-brand-400 mb-3 size-4" />
                  <div className="text-sm font-medium text-neutral-100">{t as string}</div>
                  <div className="mt-1 text-xs leading-relaxed text-neutral-500">{b as string}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Argus — the depth / differentiator */}
      <section id="why" className="border-t border-neutral-900">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center">
            <SectionLabel>Why Argus</SectionLabel>
            <h2 className="mx-auto max-w-2xl text-balance text-3xl font-semibold tracking-tight text-neutral-50">
              Intelligence no spreadsheet — and no general-purpose AI — can give you
            </h2>
          </div>

          <div className="mt-12 grid gap-4 lg:grid-cols-3">
            {/* Hero tile — conflict radar */}
            <div className="lg:col-span-2 lg:row-span-2">
              <div className="flex h-full flex-col rounded-2xl border border-neutral-800/80 bg-neutral-900/40 p-6">
                <div className="flex items-center gap-2.5">
                  <div className="bg-brand-950/60 text-brand-300 flex size-9 items-center justify-center rounded-lg">
                    <Radar className="size-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-50">Conflict radar</h3>
                </div>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-neutral-400">
                  Argus continuously evaluates every LP&apos;s MFN clause against every other
                  negotiated term across the book. The instant a later side letter grants something
                  better, the earlier holder&apos;s entitlement surfaces as an active breach — the
                  conflict a partner review would miss.
                </p>
                <div className="mt-5 grow">
                  <ProductFrame
                    src="/marketing/dashboard.png"
                    alt="Conflict radar surfacing an active MFN breach with a compliance posture score"
                  />
                </div>
              </div>
            </div>

            {/* What-if */}
            <div className="rounded-2xl border border-neutral-800/80 bg-neutral-900/40 p-6">
              <div className="flex items-center gap-2.5">
                <div className="bg-brand-950/60 text-brand-300 flex size-9 items-center justify-center rounded-lg">
                  <FlaskConical className="size-5" />
                </div>
                <h3 className="text-base font-semibold text-neutral-50">What-if simulator</h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                Before you grant a fee break or a co-invest right, model it. Argus shows exactly who
                could elect the same term — and the commitment it exposes — before it&apos;s in a
                signed letter.
              </p>
            </div>

            {/* Audit */}
            <div className="rounded-2xl border border-neutral-800/80 bg-neutral-900/40 p-6">
              <div className="flex items-center gap-2.5">
                <div className="bg-brand-950/60 text-brand-300 flex size-9 items-center justify-center rounded-lg">
                  <ScrollText className="size-5" />
                </div>
                <h3 className="text-base font-semibold text-neutral-50">Audit-grade trail</h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                Every extraction, review, draft, and resolution is written to an append-only log.
                Export a per-fund evidence pack in one click.
              </p>
            </div>
          </div>

          {/* Secondary product rows */}
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-neutral-800/80 bg-neutral-900/40 p-6">
              <div className="mb-4 flex items-center gap-2.5">
                <FlaskConical className="text-brand-400 size-4" />
                <h3 className="text-sm font-semibold text-neutral-100">
                  Simulate a term before you grant it
                </h3>
              </div>
              <ProductFrame
                src="/marketing/simulator.png"
                alt="What-if simulator showing which LPs become entitled and the commitment exposed"
              />
            </div>
            <div className="rounded-2xl border border-neutral-800/80 bg-neutral-900/40 p-6">
              <div className="mb-4 flex items-center gap-2.5">
                <GitCompareArrows className="text-brand-400 size-4" />
                <h3 className="text-sm font-semibold text-neutral-100">
                  Every negotiated term, one row per LP
                </h3>
              </div>
              <ProductFrame
                src="/marketing/matrix.png"
                alt="LP comparison matrix with click-to-source provisions"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-neutral-900 bg-neutral-950/30">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="text-center">
            <SectionLabel>How it works</SectionLabel>
            <h2 className="text-3xl font-semibold tracking-tight text-neutral-50">
              From scattered PDFs and deadlines to a defensible compliance position
            </h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[
              [
                FileSearch,
                "Set up & ingest",
                "Add your funds and LPs, instantiate the SEC/IRS requirements that apply, and upload your side letters. Argus extracts every provision into a locked 23-category taxonomy with a source-linked excerpt and a confidence score.",
              ],
              [
                Radar,
                "Detect, track & draft",
                "Argus fires MFN conflicts ranked by severity with the clause cited, builds your deadline calendar, and auto-populates filings like Form PF — drafting the rest with AI.",
              ],
              [
                ShieldCheck,
                "Verify & record",
                "Your team confirms or corrects every finding and filing; nothing is authoritative until a person signs off; every action lands in the append-only audit trail.",
              ],
            ].map(([Icon, t, b], i) => {
              const I = Icon as React.ComponentType<{ className?: string }>;
              return (
                <div
                  key={t as string}
                  className="relative rounded-xl border border-neutral-800/80 bg-neutral-900/40 p-6"
                >
                  <div className="mb-3 flex items-center gap-3">
                    <span className="border-brand-700/50 bg-brand-950/40 text-brand-300 flex size-8 items-center justify-center rounded-full border text-sm font-semibold tabular-nums">
                      {i + 1}
                    </span>
                    <I className="size-4 text-neutral-500" />
                  </div>
                  <h3 className="text-base font-medium text-neutral-100">{t as string}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-400">{b as string}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust / accuracy */}
      <section className="border-y border-neutral-900 bg-neutral-950/30">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="grid gap-10 md:grid-cols-[0.9fr_1.1fr] md:items-start">
            <div>
              <SectionLabel>Built to be trusted</SectionLabel>
              <h2 className="text-3xl font-semibold tracking-tight text-neutral-50">
                What if the AI is wrong?
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-neutral-400">
                The right answer in a fiduciary product isn&apos;t &ldquo;trust us, it&apos;s
                accurate.&rdquo; It&apos;s a system that shows its work and keeps a person in the
                loop. Argus is designed around that from the ground up.
              </p>
            </div>
            <ul className="space-y-5">
              {[
                [
                  Eye,
                  "Human-in-the-loop",
                  "Every AI extraction and draft carries a confidence score and a review state. Nothing is authoritative until a person signs off.",
                ],
                [
                  FileSearch,
                  "Cites the exact clause",
                  "Each flagged conflict links to the verbatim source text and page. One click from a finding to the contract.",
                ],
                [
                  ScrollText,
                  "Append-only audit trail",
                  "Update and delete are revoked on the log at the database level. The record cannot be quietly rewritten.",
                ],
                [
                  GitCompareArrows,
                  "Accuracy is gated in CI",
                  "Every prompt version ships with a versioned eval set, and releases are gated on extraction, MFN, and filing accuracy thresholds before they merge.",
                ],
              ].map(([Icon, t, b]) => {
                const I = Icon as React.ComponentType<{ className?: string }>;
                return (
                  <li key={t as string} className="flex gap-4">
                    <div className="text-brand-300 mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border border-neutral-800 bg-neutral-900/60">
                      <I className="size-4" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-neutral-100">{t as string}</div>
                      <div className="mt-1 text-sm leading-relaxed text-neutral-400">
                        {b as string}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="mx-auto max-w-5xl px-6 py-20">
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
          <StatCounter to={100} suffix="%" label="of AI findings cited to a source clause" />
          <StatCounter to={23} label="provision categories in one locked taxonomy" />
          <StaticStat value="9–12mo" label="manual MFN cycle Argus compresses to minutes" />
          <StaticStat value="$50k+" label="outside-counsel spend displaced per MFN cycle" />
        </div>
      </section>

      {/* Security */}
      <section id="security" className="border-t border-neutral-900 bg-neutral-950/30">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="text-center">
            <SectionLabel>Security & data</SectionLabel>
            <h2 className="text-3xl font-semibold tracking-tight text-neutral-50">
              Built for the documents that matter most
            </h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              [
                Lock,
                "Encrypted in transit & at rest",
                "TLS 1.2+ in transit; encryption at rest on managed Postgres.",
              ],
              [
                Sparkles,
                "Your data is never trained on",
                "Documents are processed for your tenant only — never used to train models.",
              ],
              [
                ShieldCheck,
                "Tenant isolation by RLS",
                "Postgres row-level security enforces separation on every read and write, verified by isolation tests on every change.",
              ],
              [
                ScrollText,
                "Append-only audit",
                "A complete, tamper-evident record of every state change in your account.",
              ],
            ].map(([Icon, t, b]) => {
              const I = Icon as React.ComponentType<{ className?: string }>;
              return (
                <div
                  key={t as string}
                  className="rounded-xl border border-neutral-800/80 bg-neutral-900/40 p-5"
                >
                  <I className="text-brand-400 mb-3 size-4" />
                  <div className="text-sm font-medium text-neutral-100">{t as string}</div>
                  <div className="mt-1 text-xs leading-relaxed text-neutral-500">{b as string}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founding design partners + roadmap (honest, forward-looking) */}
      <section className="mx-auto max-w-5xl px-6 py-20">
        <div className="rounded-2xl border border-neutral-800/80 bg-neutral-900/40 p-8 sm:p-10">
          <div className="grid gap-8 md:grid-cols-[1.1fr_0.9fr] md:items-center">
            <div>
              <SectionLabel>Founding design partners</SectionLabel>
              <h2 className="text-balance text-2xl font-semibold tracking-tight text-neutral-50 sm:text-3xl">
                Be one of the first funds on Argus
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-neutral-400">
                Argus is built, and we&apos;re selecting a small group of founding design partners —
                fund CCOs and GCs at mid-market private equity managers — to take it to production.
                Founding partners lock in founding-customer pricing, get white-glove onboarding, and
                have a direct line to the team that shapes what we build next.
              </p>
              <div className="mt-6">
                <a href={CONTACT}>
                  <Button variant="primary" size="md">
                    Become a design partner <ArrowRight className="size-4" />
                  </Button>
                </a>
              </div>
            </div>
            <div className="rounded-xl border border-neutral-800/80 bg-neutral-950/40 p-6">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                On the roadmap
              </div>
              <ul className="mt-4 space-y-3 text-sm text-neutral-300">
                {[
                  "Live fund-admin & LP-portal integrations",
                  "Semantic search across your full document corpus",
                  "Form ADV and more regulatory filings",
                  "SOC 2 Type II",
                ].map((r) => (
                  <li key={r} className="flex items-start gap-2.5">
                    <span className="bg-brand-500 mt-1.5 size-1.5 shrink-0 rounded-full" />
                    {r}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs leading-relaxed text-neutral-600">
                Where we&apos;re headed with funding and a production team — not shipped yet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-neutral-900 bg-neutral-950/30">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center">
            <SectionLabel>Pricing</SectionLabel>
            <h2 className="text-3xl font-semibold tracking-tight text-neutral-50">
              Priced per fund, per year
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400">
              A single manual MFN cycle runs $50k–$250k in outside counsel — and it&apos;s one of a
              dozen obligations Argus handles.{" "}
              <span className="text-neutral-200">
                Run your whole compliance program for a fraction — every year.
              </span>
            </p>
          </div>

          <div className="mt-12 grid items-stretch gap-5 lg:grid-cols-3">
            <PriceCard
              name="Solo"
              price="$15,000"
              unit="/ fund / year"
              tagline="For the single-fund manager with an in-house CCO or GC."
              features={[
                "1 active fund, up to ~40 LPs",
                "Compliance calendar — every regulatory & side-letter deadline",
                "Side-letter extraction + MFN conflict detection",
                "Form PF filing workspace (AI draft → filing-ready)",
                "Code of ethics + pay-to-play screening",
                "What-if simulator & audit evidence pack",
              ]}
              cta="Explore the demo"
              to={DEMO_TO}
            />
            <PriceCard
              name="Core"
              price="$12,000"
              unit="/ fund / year"
              highlighted
              tagline="The multi-fund sweet spot. Price per fund drops as you add funds."
              footnote="2–6 funds · from $30,000 / year"
              features={[
                "Everything in Solo, on every fund",
                "Unlimited LPs per fund",
                "Cross-fund MFN conflict detection",
                "Firm-wide compliance dashboard",
                "SSO + role-based access",
                "Priority support",
              ]}
              cta="Book a walkthrough"
              to={CONTACT}
              external
            />
            <PriceCard
              name="Enterprise"
              price="Let's talk"
              unit=""
              tagline="For platforms with 7+ funds and complex integration needs."
              features={[
                "Everything in Core",
                "Form ADV & custom filing templates",
                "Custom LP-portal / fund-admin integrations",
                "Bespoke SEC-exam evidence packs",
                "Dedicated customer success + SLA",
              ]}
              cta="Contact sales"
              to={CONTACT}
              external
            />
          </div>
          <p className="mt-6 text-center text-xs text-neutral-600">
            Annual billing. Founding-customer pricing available for design partners.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-6 py-20">
        <div className="text-center">
          <SectionLabel>FAQ</SectionLabel>
          <h2 className="text-3xl font-semibold tracking-tight text-neutral-50">
            Questions GCs and CCOs ask
          </h2>
        </div>
        <div className="mt-10 space-y-3">
          {FAQS.map((f) => (
            <details
              key={f.q}
              className="group rounded-xl border border-neutral-800/80 bg-neutral-900/40 px-5 py-4 [&_summary]:cursor-pointer"
            >
              <summary className="flex items-center justify-between gap-4 text-sm font-medium text-neutral-100 marker:content-['']">
                {f.q}
                <span className="text-neutral-500 transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="argus-mesh relative overflow-hidden border-t border-neutral-900">
        <Grain />
        <div className="relative mx-auto max-w-3xl px-6 py-24 text-center">
          <h2 className="text-balance text-4xl font-semibold tracking-tight text-neutral-50">
            Know every obligation. Prove every decision.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to={DEMO_TO}>
              <Button variant="primary" size="md">
                Explore the live demo <ArrowRight className="size-4" />
              </Button>
            </Link>
            <a href={CONTACT}>
              <Button variant="secondary" size="md">
                Book a walkthrough
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-975 border-t border-neutral-900">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-3">
              <HenosisWordmark className="h-6" />
              <p className="max-w-xs text-xs leading-relaxed text-neutral-500">
                The AI compliance platform for private fund GPs. Side letters, filings, deadlines,
                and conduct — one system of record, built for the SEC exam.
              </p>
            </div>
            <FooterCol
              title="Platform"
              links={[
                ["Compliance calendar", "#platform"],
                ["Regulatory filings", "#platform"],
                ["Why Argus", "#why"],
                ["How it works", "#how"],
              ]}
            />
            <FooterCol
              title="Trust"
              links={[
                ["Security", "#security"],
                ["Pricing", "#pricing"],
                ["FAQ", "#faq"],
              ]}
            />
            <FooterCol
              title="Company"
              links={[
                ["Live demo", "/sign-in"],
                ["Contact", CONTACT],
              ]}
            />
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-900 pt-6 text-xs text-neutral-600">
            <span>© 2026 Henosis. Henosis Argus.</span>
            <span className="flex items-center gap-4">
              <span>Powered by Claude</span>
              <span>SOC 2 on our roadmap</span>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------------------------------------------------------- ModuleCard */

function ModuleCard({
  icon: Icon,
  title,
  body,
  src,
  alt,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  src: string;
  alt: string;
}) {
  return (
    <div className="flex flex-col rounded-2xl border border-neutral-800/80 bg-neutral-900/40 p-6">
      <div className="flex items-center gap-2.5">
        <div className="bg-brand-950/60 text-brand-300 flex size-9 items-center justify-center rounded-lg">
          <Icon className="size-5" />
        </div>
        <h3 className="text-base font-semibold text-neutral-50">{title}</h3>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-neutral-400">{body}</p>
      <div className="mt-5">
        <ProductFrame src={src} alt={alt} />
      </div>
    </div>
  );
}

function PriceCard({
  name,
  price,
  unit,
  tagline,
  features,
  cta,
  to,
  footnote,
  highlighted,
  external,
}: {
  name: string;
  price: string;
  unit: string;
  tagline: string;
  features: string[];
  cta: string;
  to: string;
  footnote?: string;
  highlighted?: boolean;
  external?: boolean;
}) {
  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-6 ${
        highlighted
          ? "border-brand-600/60 bg-brand-950/20 shadow-brand-950/30 shadow-xl"
          : "border-neutral-800/80 bg-neutral-900/40"
      }`}
    >
      {highlighted && (
        <span className="bg-brand-600 absolute -top-3 left-6 rounded-full px-2.5 py-0.5 text-[11px] font-semibold text-white">
          Most popular
        </span>
      )}
      <div className="text-sm font-semibold text-neutral-100">{name}</div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-3xl font-semibold tabular-nums tracking-tight text-neutral-50">
          {price}
        </span>
        {unit && <span className="text-sm text-neutral-500">{unit}</span>}
      </div>
      {footnote && <div className="mt-1 text-xs text-neutral-500">{footnote}</div>}
      <p className="mt-3 text-sm leading-relaxed text-neutral-400">{tagline}</p>
      <ul className="mt-5 grow space-y-2.5">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-neutral-300">
            <Check className="mt-0.5 size-4 shrink-0 text-emerald-400" />
            {f}
          </li>
        ))}
      </ul>
      <div className="mt-6">
        {external ? (
          <a href={to}>
            <Button variant={highlighted ? "primary" : "secondary"} className="w-full">
              {cta}
            </Button>
          </a>
        ) : (
          <Link to={to}>
            <Button variant={highlighted ? "primary" : "secondary"} className="w-full">
              {cta}
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function FooterCol({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{title}</div>
      <ul className="space-y-2 text-sm text-neutral-500">
        {links.map(([label, href]) => (
          <li key={label}>
            <a href={href} className="transition hover:text-neutral-200">
              {label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

const FAQS: { q: string; a: string }[] = [
  {
    q: "What compliance areas does Argus cover?",
    a: "Argus is a full compliance platform for private fund GPs. It covers side-letter extraction and MFN conflict detection, a unified deadline calendar across regulatory and side-letter obligations, regulatory filings (Form PF today, Form ADV next), code-of-ethics attestation campaigns, political-contribution / pay-to-play screening, and a requirements catalog with checklists — all on an append-only audit trail.",
  },
  {
    q: "How accurate is the AI?",
    a: "Argus pairs a deterministic rules engine with Claude-powered extraction and drafting. Every prompt version ships with a versioned eval set, and releases are gated in CI on extraction, MFN, and filing precision/recall thresholds. The conflict, what-if, and calendar logic is deterministic — it runs even without the AI layer — and every finding cites the exact source clause for a human to confirm.",
  },
  {
    q: "Does Argus file with the SEC for me?",
    a: "Argus gets you filing-ready, not auto-filed. It auto-populates a filing like Form PF from your platform data, drafts the judgment fields with AI, and flags what it can't know as gaps for you to complete. You review and submit through the SEC's own systems (PFRD/IARD), which don't permit third-party submission — so the final filing is always your team's decision.",
  },
  {
    q: "Is the AI making decisions on its own?",
    a: "Never. Argus is human-in-the-loop by design. Every extracted provision and drafted field carries a confidence score and a review state, and nothing is treated as authoritative until a person on your team confirms it. The AI surfaces, drafts, and cites; your team decides.",
  },
  {
    q: "Where does my data go? Do you train on it?",
    a: "No. Your documents are processed for your tenant only and are never used to train any model. Data is encrypted in transit (TLS 1.2+) and at rest on managed Postgres, with tenant isolation enforced by row-level security on every query.",
  },
  {
    q: "How does the audit trail work?",
    a: "Every state-changing action — extraction, review, resolution, filing edit, attestation, obligation completion — is written to an append-only log. Update and delete are revoked at the database level, so the record cannot be quietly rewritten. You can export a per-fund evidence pack for an SEC exam or LP due-diligence questionnaire in one click.",
  },
  {
    q: "What does Argus integrate with?",
    a: "Argus runs as a standalone system of record today and is built to connect with fund-administration and LP-portal systems. Custom integrations are part of the Enterprise tier — tell us your stack and we'll scope it.",
  },
  {
    q: "How long does onboarding take?",
    a: "Add your funds, instantiate the requirements that apply, and upload your executed side letters — Argus extracts and cross-references them immediately. A typical fund is reviewed and live in days, not the months a manual MFN compendium takes.",
  },
];
